import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getValidToken, API_URL } from '../../utils/authUtils';

const BACKEND_URL = API_URL;

// ── Color constants ──────────────────────────────────────
const EMERALD   = '#059669';
const VIOLET    = '#7c3aed';
const AMBER     = '#d97706';
const SKY       = '#0284c7';
const ROSE      = '#e11d48';
const GRAY      = '#6b7280';
const CYAN      = '#0891b2';

const URGENCY_COLORS = {
  CRITICAL: 'bg-red-600 text-white',
  HIGH:     'bg-orange-500 text-white',
  STANDARD: 'bg-blue-500 text-white',
  LOW:      'bg-gray-400 text-white',
};

const FINDING_COLORS = {
  CONTRADICTION:    'bg-red-100 text-red-800 border-red-200',
  AI_WASHING:       'bg-purple-100 text-purple-800 border-purple-200',
  OMISSION:         'bg-yellow-100 text-yellow-800 border-yellow-200',
  LEADING_INDICATOR:'bg-blue-100 text-blue-800 border-blue-200',
  CORROBORATION:    'bg-green-100 text-green-800 border-green-200',
  CONTEXT:          'bg-cyan-100 text-cyan-800 border-cyan-200',
  CLEAR:            'bg-gray-100 text-gray-600 border-gray-200',
};

const SOURCE_LABELS = {
  WARN: 'WARN Act',
  SEC_8K: 'SEC 8-K',
  FMP_NEWS: 'FMP Stock News',
  FMP_PRESS_RELEASE: 'FMP Press Release',
};

// ── Helpers ──────────────────────────────────────────────
const getHeaders = () => ({ Authorization: `Bearer ${getValidToken()}` });

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtMoney = (v) => {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

const fmtNum = (v) => v != null ? v.toLocaleString() : '—';

const fmtPct = (v) => {
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`;
};

// ── Simple markdown renderer ─────────────────────────────
// Converts markdown-ish text to JSX with headings, bold, lists, links
const RenderMarkdown = ({ text }) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let listType = null; // 'ul' or 'ol'

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === 'ol') {
        elements.push(<ol key={`ol-${elements.length}`} className="list-decimal list-inside space-y-1 mb-4 text-gray-700">{listItems}</ol>);
      } else {
        elements.push(<ul key={`ul-${elements.length}`} className="list-disc list-inside space-y-1 mb-4 text-gray-700">{listItems}</ul>);
      }
      listItems = [];
      listType = null;
    }
  };

  const renderInline = (str) => {
    // Bold: **text**
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) {
        return <strong key={i}>{p.slice(2, -2)}</strong>;
      }
      return p;
    });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Headings
    if (line.startsWith('### ')) {
      flushList();
      elements.push(<h3 key={i} className="text-lg font-bold text-gray-900 mt-6 mb-2">{renderInline(line.slice(4))}</h3>);
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(<h2 key={i} className="text-xl font-bold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">{renderInline(line.slice(3))}</h2>);
    } else if (line.startsWith('# ')) {
      flushList();
      // Skip top-level heading — we render it in the header
    } else if (line.match(/^[-*] /)) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(<li key={i}>{renderInline(line.replace(/^[-*] /, ''))}</li>);
    } else if (line.match(/^\d+\. /)) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(<li key={i}>{renderInline(line.replace(/^\d+\. /, ''))}</li>);
    } else if (line.startsWith('---') || line.startsWith('***')) {
      flushList();
      elements.push(<hr key={i} className="my-6 border-gray-200" />);
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      elements.push(<p key={i} className="text-gray-700 mb-3 leading-relaxed">{renderInline(line)}</p>);
    }
  }
  flushList();
  return <div>{elements}</div>;
};


// ══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════
const SWEEReportViewer = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/swee/reports/${reportId}/viewer`,
          { headers: getHeaders() }
        );
        setReport(data);
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500 text-lg">Loading report...</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">{error || 'Report not found'}</div>
          <button onClick={() => navigate('/admin/swee?tab=reports')} className="text-blue-600 hover:underline">
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  const evidence = report.evidence_summary || {};
  const findings = report.findings || [];
  const financials = report.financials || [];
  const externalSignals = report.external_signals || [];
  const tracker = report.tracker;
  const trackerSeniority = report.tracker_seniority || [];
  const trackerTechnologies = report.tracker_technologies || [];
  const trackerSampleRoles = report.tracker_sample_roles || [];
  const linkedinInsights = report.linkedin_insights;
  const linkedinBreakdown = report.linkedin_hiring_breakdown || [];
  const srcAvail = evidence.sources_available || report.sources_available || [];
  const srcMissing = evidence.sources_missing || [];
  const empData = evidence.employee_data;
  const fcfData = evidence.fcf_snapshot;

  // Financial chart data (most recent 8 periods, reversed to chronological)
  const chartData = [...financials].reverse().slice(-8).map(f => ({
    period: f.fiscal_period,
    revenue: f.revenue ? f.revenue / 1e9 : null,
    net_income: f.net_income ? f.net_income / 1e9 : null,
    fcf: f.free_cash_flow ? f.free_cash_flow / 1e9 : null,
    cash: f.cash_and_equivalents ? f.cash_and_equivalents / 1e9 : null,
    rev_per_emp: f.revenue_per_employee ? Math.round(f.revenue_per_employee) : null,
  }));

  // Annual headcount data (FY only, 2020+) — matches SEC Metrics Workforce chart
  const annualEmpData = [...financials]
    .filter(f => f.fiscal_period && f.fiscal_period.includes('-FY') && f.employee_count)
    .filter(f => {
      const year = parseInt(f.fiscal_period.split('-')[0], 10);
      return year >= 2020;
    })
    .sort((a, b) => a.fiscal_period.localeCompare(b.fiscal_period))
    .map(f => ({
      period: f.fiscal_period.replace('-FY', ''),
      employee_count: f.employee_count,
    }));

  // Annual cash flow data (FY only, 2020+) — 4 metrics
  const annualCfData = [...financials]
    .filter(f => f.fiscal_period && f.fiscal_period.includes('-FY'))
    .filter(f => {
      const year = parseInt(f.fiscal_period.split('-')[0], 10);
      return year >= 2020;
    })
    .filter(f => f.cash_and_equivalents || f.operating_cash_flow || f.free_cash_flow || f.capital_expenditure)
    .sort((a, b) => a.fiscal_period.localeCompare(b.fiscal_period))
    .map(f => ({
      period: f.fiscal_period.replace('-FY', ''),
      cash: f.cash_and_equivalents ? f.cash_and_equivalents / 1e9 : null,
      ocf: f.operating_cash_flow ? f.operating_cash_flow / 1e9 : null,
      fcf: f.free_cash_flow ? f.free_cash_flow / 1e9 : null,
      capex: f.capital_expenditure ? f.capital_expenditure / 1e9 : null,
    }));

  // Annual efficiency data (FY only, 2020+) — SG&A%, Rev/Employee, OCF/Employee
  const annualEffData = [...financials]
    .filter(f => f.fiscal_period && f.fiscal_period.includes('-FY'))
    .filter(f => {
      const year = parseInt(f.fiscal_period.split('-')[0], 10);
      return year >= 2020;
    })
    .filter(f => f.sga_revenue_ratio || f.revenue_per_employee || (f.operating_cash_flow && f.employee_count))
    .sort((a, b) => a.fiscal_period.localeCompare(b.fiscal_period))
    .map(f => ({
      period: f.fiscal_period.replace('-FY', ''),
      sga_pct: f.sga_revenue_ratio != null ? Number((f.sga_revenue_ratio * 100).toFixed(1)) : null,
      rev_per_emp: f.revenue_per_employee ? Math.round(f.revenue_per_employee / 1000) : null, // $K
      ocf_per_emp: (f.operating_cash_flow && f.employee_count)
        ? Math.round(f.operating_cash_flow / f.employee_count / 1000) // $K
        : null,
    }));

  // Group external signals by source type
  const signalsBySource = {};
  externalSignals.forEach(s => {
    const key = s.source_type || 'OTHER';
    if (!signalsBySource[key]) signalsBySource[key] = [];
    signalsBySource[key].push(s);
  });

  // LinkedIn hiring breakdown chart data (sorted by count desc, top 10)
  const liBreakdownData = linkedinBreakdown
    .filter(h => h.employee_count > 0)
    .slice(0, 10)
    .map(h => ({
      name: h.function_name,
      count: h.employee_count,
      pct: h.percentage,
    }));

  // LinkedIn employee count history (24-month trend from snapshot)
  // The actor may return this as a dict {date: count}, an array of objects, or empty
  const liHistoryRaw = linkedinInsights?.employee_count_history;
  const liHistoryData = (() => {
    if (!liHistoryRaw || typeof liHistoryRaw !== 'object') return [];
    // Dict format: {"2024-01": 50000, "2024-02": 51000}
    if (!Array.isArray(liHistoryRaw)) {
      return Object.entries(liHistoryRaw)
        .filter(([, count]) => count != null && typeof count === 'number')
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
    }
    // Array format: [{date: "2024-3-1", employee_count: 305139}, ...]
    if (liHistoryRaw.length > 0 && liHistoryRaw[0]?.date != null) {
      return liHistoryRaw
        .filter(h => h.date && (h.employee_count != null || h.count != null))
        .map(h => ({ date: h.date, count: h.employee_count ?? h.count }))
        .sort((a, b) => a.date.localeCompare(b.date));
    }
    return [];
  })();

  return (
    <div className="min-h-screen bg-white">
      {/* ── Navigation bar (hidden in print) ── */}
      <div className="bg-gray-50 border-b print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/admin/swee?tab=reports')}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            <span>&larr;</span> Back to Reports
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                setPdfLoading(true);
                try {
                  const resp = await axios.get(
                    `${BACKEND_URL}/api/swee/reports/${reportId}/pdf`,
                    { headers: getHeaders(), responseType: 'blob' }
                  );
                  const url = window.URL.createObjectURL(resp.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `SWEE_${report?.ticker || 'report'}_${reportId.slice(0, 8)}.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  console.error('PDF download failed:', err);
                  alert('PDF generation failed. You can use Print / Save PDF instead.');
                } finally {
                  setPdfLoading(false);
                }
              }}
              disabled={pdfLoading}
              className="px-4 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {pdfLoading ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              onClick={async () => {
                try {
                  const { data } = await axios.get(
                    `${BACKEND_URL}/api/swee/reports/${reportId}/audio`,
                    { headers: getHeaders() }
                  );
                  if (data?.url) window.open(data.url, '_blank');
                  else alert('No audio URL returned');
                } catch (err) {
                  alert(err.response?.data?.detail || 'Earnings call audio not available');
                }
              }}
              className="px-4 py-1.5 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600"
            >
              Earnings Call Audio
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              Print
            </button>
          </div>
        </div>
      </div>

      {/* ── Report content ── */}
      <div className="max-w-5xl mx-auto px-6 py-8 print:px-0 print:py-4">

        {/* ══ HEADER ══ */}
        <header className="mb-8">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-3xl font-bold text-gray-900 font-mono">{report.ticker}</span>
                <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${URGENCY_COLORS[report.urgency] || URGENCY_COLORS.STANDARD}`}>
                  {report.urgency}
                </span>
                {report.is_sp500 && (
                  <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">S&P 500</span>
                )}
              </div>
              <h1 className="text-xl text-gray-700 font-medium">{report.company_name || ''}</h1>
              {report.sector && (
                <div className="text-sm text-gray-400 mt-1">{report.sector}{report.sub_industry ? ` / ${report.sub_industry}` : ''}</div>
              )}
            </div>
            <div className="text-right text-sm text-gray-500 flex-shrink-0">
              <div className="font-medium">{fmtDate(report.created_at)}</div>
              <div className="text-xs text-gray-400 mt-1">
                {srcAvail.length}/9 sources
              </div>
            </div>
          </div>
          <h2 className="text-lg font-semibold text-gray-800">{report.headline}</h2>
        </header>

        {/* ══ KEY METRICS CARDS ══ */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard
            label="Employees"
            value={empData?.current ? fmtNum(empData.current) : (financials[0]?.employee_count ? fmtNum(financials[0].employee_count) : '—')}
            sub={empData?.yoy_change != null ? `YoY: ${empData.yoy_change > 0 ? '+' : ''}${fmtNum(empData.yoy_change)} (${fmtPct(empData.yoy_change_pct)})` : null}
            subColor={empData?.yoy_change > 0 ? 'text-green-600' : empData?.yoy_change < 0 ? 'text-red-600' : 'text-gray-500'}
          />
          <MetricCard
            label="Revenue"
            value={financials[0]?.revenue ? fmtMoney(financials[0].revenue) : '—'}
            sub={financials[0]?.fiscal_period}
          />
          <MetricCard
            label="Cash Position"
            value={financials[0]?.cash_and_equivalents ? fmtMoney(financials[0].cash_and_equivalents) : '—'}
            sub={financials[0]?.fiscal_period}
          />
          <MetricCard
            label="Free Cash Flow"
            value={fcfData?.free_cash_flow != null ? fmtMoney(fcfData.free_cash_flow) : (financials[0]?.free_cash_flow != null ? fmtMoney(financials[0].free_cash_flow) : '—')}
            sub={fcfData?.fcf_yield != null ? `Yield: ${(fcfData.fcf_yield * 100).toFixed(1)}%` : null}
          />
        </section>

        {/* ══ HIRING TRACKER SUMMARY ══ */}
        {tracker && (
          <section className="mb-8 bg-blue-50 rounded-lg border border-blue-200 p-4 print:bg-white print:border-gray-300">
            <h3 className="text-sm font-bold text-blue-900 mb-3">BroadestGauge</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <div className="text-xs text-blue-600">Open Jobs</div>
                <div className="font-bold text-blue-900">{fmtNum(tracker.latest_num_jobs)}</div>
              </div>
              <div>
                <div className="text-xs text-blue-600">New (30d)</div>
                <div className="font-bold text-blue-900">{fmtNum(tracker.latest_num_jobs_last_30)}</div>
              </div>
              <div>
                <div className="text-xs text-blue-600">Refresh Rate</div>
                <div className="font-bold text-blue-900">{tracker.latest_refresh_rate != null ? `${Number(tracker.latest_refresh_rate).toFixed(1)}%` : '—'}</div>
              </div>
              <div>
                <div className="text-xs text-blue-600">MoM Change</div>
                <div className={`font-bold ${tracker.mom_change_pct > 0 ? 'text-green-700' : tracker.mom_change_pct < 0 ? 'text-red-700' : 'text-blue-900'}`}>
                  {tracker.mom_change_pct != null ? fmtPct(tracker.mom_change_pct) : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-blue-600">Velocity</div>
                <div className="font-bold text-blue-900">
                  {tracker.velocity_direction && tracker.velocity_direction !== 'INSUFFICIENT_DATA'
                    ? tracker.velocity_direction
                    : <span className="text-gray-400 font-normal">Insufficient data</span>}
                </div>
              </div>
            </div>

            {/* Seniority + Technology side by side */}
            {(trackerSeniority.length > 0 || trackerTechnologies.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* Seniority Breakdown */}
                {trackerSeniority.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-blue-700 mb-2">Seniority Breakdown</div>
                    <div className="space-y-1">
                      {trackerSeniority.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="text-gray-700 capitalize">{s.seniority?.replace(/_/g, ' ') || 'Unknown'}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-blue-100 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${Math.min(s.percentage || 0, 100)}%` }}
                              />
                            </div>
                            <span className="text-gray-500 w-16 text-right">{fmtNum(s.count)} ({s.percentage != null ? `${Number(s.percentage).toFixed(0)}%` : '—'})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Technologies */}
                {trackerTechnologies.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-blue-700 mb-2">Top Technologies</div>
                    <div className="flex flex-wrap gap-1.5">
                      {trackerTechnologies.map((t, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                          {t.technology_slug}
                          <span className="text-blue-500 text-[10px]">({t.mention_count})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sample Roles */}
            {trackerSampleRoles.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-blue-700 mb-2">Sample Roles</div>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead>
                      <tr className="text-left text-blue-600 border-b border-blue-200">
                        <th className="py-1 pr-3">Title</th>
                        <th className="py-1 pr-3">Seniority</th>
                        <th className="py-1 pr-3">Location</th>
                        <th className="py-1 pr-3">Salary</th>
                        <th className="py-1">Posted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trackerSampleRoles.map((r, i) => (
                        <tr key={i} className="border-b border-blue-50">
                          <td className="py-1 pr-3 text-gray-800 max-w-[200px] truncate">{r.job_title}</td>
                          <td className="py-1 pr-3 text-gray-600 capitalize">{r.seniority?.replace(/_/g, ' ') || '—'}</td>
                          <td className="py-1 pr-3 text-gray-600">
                            {r.location || '—'}
                            {r.remote && <span className="ml-1 text-green-600">(R)</span>}
                            {r.hybrid && <span className="ml-1 text-amber-600">(H)</span>}
                          </td>
                          <td className="py-1 pr-3 text-gray-600">
                            {r.min_salary || r.max_salary
                              ? `${r.min_salary ? `$${(r.min_salary/1000).toFixed(0)}K` : '?'}–${r.max_salary ? `$${(r.max_salary/1000).toFixed(0)}K` : '?'}`
                              : '—'}
                          </td>
                          <td className="py-1 text-gray-500">{r.date_posted ? fmtDate(r.date_posted) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ══ LINKEDIN COMPANY INSIGHTS ══ */}
        {linkedinInsights && (
          <section className="mb-8 bg-sky-50 rounded-lg border border-sky-200 p-4 print:bg-white print:border-gray-300">
            <h3 className="text-sm font-bold text-sky-900 mb-3">LinkedIn Data</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <div className="text-xs text-sky-600">LinkedIn Headcount</div>
                <div className="font-bold text-sky-900">{linkedinInsights.employee_count != null ? fmtNum(linkedinInsights.employee_count) : '—'}</div>
              </div>
              <div>
                <div className="text-xs text-sky-600">6-Month Growth</div>
                <div className={`font-bold ${linkedinInsights.growth_6m_pct > 0 ? 'text-green-700' : linkedinInsights.growth_6m_pct < 0 ? 'text-red-700' : 'text-sky-900'}`}>
                  {linkedinInsights.growth_6m_pct != null ? fmtPct(linkedinInsights.growth_6m_pct) : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-sky-600">1-Year Growth</div>
                <div className={`font-bold ${linkedinInsights.growth_1y_pct > 0 ? 'text-green-700' : linkedinInsights.growth_1y_pct < 0 ? 'text-red-700' : 'text-sky-900'}`}>
                  {linkedinInsights.growth_1y_pct != null ? fmtPct(linkedinInsights.growth_1y_pct) : '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-sky-600">2-Year Growth</div>
                <div className={`font-bold ${linkedinInsights.growth_2y_pct > 0 ? 'text-green-700' : linkedinInsights.growth_2y_pct < 0 ? 'text-red-700' : 'text-sky-900'}`}>
                  {linkedinInsights.growth_2y_pct != null ? fmtPct(linkedinInsights.growth_2y_pct) : '—'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-xs text-sky-600">Median Tenure</div>
                <div className="font-bold text-sky-900">{linkedinInsights.median_tenure_months != null ? `${Number(linkedinInsights.median_tenure_months).toFixed(1)} mo` : '—'}</div>
              </div>
              <div>
                <div className="text-xs text-sky-600">Job Openings</div>
                <div className="font-bold text-sky-900">{linkedinInsights.total_job_openings != null ? fmtNum(linkedinInsights.total_job_openings) : '—'}</div>
              </div>
              <div>
                <div className="text-xs text-sky-600">New Hires</div>
                <div className="font-bold text-sky-900">{linkedinInsights.new_hires_count != null ? fmtNum(linkedinInsights.new_hires_count) : '—'}</div>
              </div>
              <div>
                <div className="text-xs text-sky-600">Notable Alumni</div>
                <div className="font-bold text-sky-900">{linkedinInsights.notable_alumni_count != null ? fmtNum(linkedinInsights.notable_alumni_count) : '—'}</div>
              </div>
            </div>
          </section>
        )}

        {/* ══ CROSS-VALIDATION FINDINGS ══ */}
        {findings.length > 0 && (
          <section className="mb-8">
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Cross-Validation Findings</h3>
            <div className="space-y-2">
              {findings.map((f, i) => {
                const colorClass = FINDING_COLORS[f.type || f.finding_type] || FINDING_COLORS.CONTEXT;
                return (
                  <div key={i} className={`rounded-lg border p-3 ${colorClass}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold uppercase">{f.type || f.finding_type}</span>
                      {f.severity && <span className="text-xs opacity-75">[{f.severity}]</span>}
                      {f.sources && (
                        <span className="text-xs opacity-60 ml-auto">{f.sources.join(', ')}</span>
                      )}
                    </div>
                    <div className="text-sm">{f.finding || f.description}</div>
                    {f.implication && (
                      <div className="text-xs mt-1 opacity-75">{f.implication}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ══ FINANCIAL CHARTS ══ */}
        {(chartData.length > 0 || liBreakdownData.length > 0 || liHistoryData.length > 1) && (
          <section className="mb-8 print:break-before-page">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">Financial Trends</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Revenue & Net Income */}
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="text-xs font-semibold text-gray-600 mb-2">Revenue & Net Income ($B)</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => v != null ? `$${v.toFixed(2)}B` : '—'} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="revenue" name="Revenue" fill={EMERALD} />
                    <Bar dataKey="net_income" name="Net Income" fill={SKY} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Cash Flow (annual FY, 2020+) — 4 metrics */}
              {annualCfData.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="text-xs font-semibold text-gray-600 mb-2">Cash Flow & Cash Position ($B, Annual 10-K)</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={annualCfData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => v != null ? `$${v.toFixed(2)}B` : '—'} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <ReferenceLine y={0} stroke="#ccc" />
                      <Bar dataKey="cash" name="Cash & Equivalents" fill={VIOLET} />
                      <Bar dataKey="ocf" name="Operating Cash Flow" fill={SKY} />
                      <Bar dataKey="fcf" name="Free Cash Flow" fill={EMERALD} />
                      <Bar dataKey="capex" name="Capex" fill={AMBER} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Headcount (annual FY, 2020+) */}
              {annualEmpData.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="text-xs font-semibold text-gray-600 mb-2">Employee Headcount (Annual 10-K)</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={annualEmpData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                      <Tooltip formatter={(v) => v != null ? v.toLocaleString() : '—'} labelStyle={{ fontSize: 11, fontWeight: 600 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="employee_count" name="Employee Count" fill={VIOLET} radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Efficiency Ratios (annual FY, 2020+) */}
              {annualEffData.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="text-xs font-semibold text-gray-600 mb-2">Efficiency Ratios (Annual 10-K)</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={annualEffData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} />
                      <YAxis yAxisId="pct" orientation="left" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={[0, 'auto']} />
                      <YAxis yAxisId="dollars" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}K`} />
                      <Tooltip formatter={(v, name) => {
                        if (name === 'SG&A % of Revenue') return v != null ? `${v.toFixed(1)}%` : '—';
                        return v != null ? `$${v.toLocaleString()}K` : '—';
                      }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar yAxisId="pct" dataKey="sga_pct" name="SG&A % of Revenue" fill={ROSE} radius={[2,2,0,0]} />
                      <Bar yAxisId="dollars" dataKey="rev_per_emp" name="Rev / Employee" fill={AMBER} radius={[2,2,0,0]} />
                      <Bar yAxisId="dollars" dataKey="ocf_per_emp" name="OCF / Employee" fill={CYAN} radius={[2,2,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* LinkedIn Headcount by Function */}
              {liBreakdownData.length > 0 && (
                <div className="bg-sky-50 rounded-lg p-4 border border-sky-200">
                  <div className="text-xs font-semibold text-sky-700 mb-2">LinkedIn Data: Headcount by Function</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={liBreakdownData} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0f2fe" />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={90} />
                      <Tooltip formatter={(v) => v != null ? v.toLocaleString() : '—'} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="count" name="Headcount" fill={SKY} radius={[0,2,2,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* LinkedIn Employee Count Trend (24-month) */}
              {liHistoryData.length > 1 && (
                <div className="bg-sky-50 rounded-lg p-4 border border-sky-200">
                  <div className="text-xs font-semibold text-sky-700 mb-2">LinkedIn Data: Employee Count Trend (24mo)</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={liHistoryData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0f2fe" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={Math.max(0, Math.floor(liHistoryData.length / 6) - 1)} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                      <Tooltip
                        formatter={(v) => v != null ? v.toLocaleString() : '—'}
                        labelFormatter={(l) => `Month: ${l}`}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="count" name="Employees" stroke={SKY} strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ══ AI REPORT NARRATIVE ══ */}
        <section className="mb-8">
          <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">Analysis</h3>
          <div className="prose prose-sm max-w-none">
            <RenderMarkdown text={report.markdown} />
          </div>
        </section>

        {/* ══ EXTERNAL SIGNALS & ARTICLES ══ */}
        {externalSignals.length > 0 && (
          <section className="mb-8 print:break-before-page">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">External Signals & Source Articles</h3>
            {Object.entries(signalsBySource).map(([sourceType, signals]) => (
              <div key={sourceType} className="mb-5">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  {SOURCE_LABELS[sourceType] || sourceType} ({signals.length})
                </h4>
                <div className="space-y-2">
                  {signals.slice(0, 10).map((s, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex-shrink-0 text-xs text-gray-400 w-20 pt-0.5">{fmtDate(s.signal_date)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {s.signal_category && (
                            <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded">
                              {s.signal_category.replace(/_/g, ' ')}
                            </span>
                          )}
                          {s.signal_direction && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              s.signal_direction === 'NEGATIVE' ? 'bg-red-100 text-red-700' :
                              s.signal_direction === 'POSITIVE' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {s.signal_direction}
                            </span>
                          )}
                        </div>
                        <div className="text-gray-800">{s.headline || s.detail_text || 'No headline'}</div>
                        {s.detail_text && s.headline && (
                          <div className="text-xs text-gray-500 mt-1 line-clamp-2">{s.detail_text}</div>
                        )}
                      </div>
                      {s.source_url && (
                        <a
                          href={s.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View source &rarr;
                        </a>
                      )}
                    </div>
                  ))}
                  {signals.length > 10 && (
                    <div className="text-xs text-gray-400 pl-3">...and {signals.length - 10} more</div>
                  )}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* ══ DATA SOURCES (hidden in print/PDF) ══ */}
        <section className="mb-8 print:hidden">
          <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Data Sources</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-semibold text-green-700 mb-1">Available ({srcAvail.length})</div>
              <div className="flex flex-wrap gap-1.5">
                {srcAvail.map((s, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded">{s}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-1">Missing ({srcMissing.length})</div>
              <div className="flex flex-wrap gap-1.5">
                {srcMissing.map((s, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-gray-50 text-gray-500 border border-gray-200 rounded">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ══ FOOTER (hidden in print/PDF) ══ */}
        <footer className="border-t border-gray-200 pt-4 mt-8 text-xs text-gray-400 flex flex-wrap gap-4 print:hidden">
          <span>Generated {fmtDate(report.created_at)}</span>
          {report.model_used && <span>Model: {report.model_used}</span>}
          {report.generation_cost_usd != null && <span>Cost: ${Number(report.generation_cost_usd).toFixed(4)}</span>}
          <span>Report ID: {report.id}</span>
        </footer>
      </div>

      {/* ── Print styles ── */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print\\:hidden { display: none !important; }
          .print\\:break-before-page { break-before: page; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:py-4 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
          .print\\:bg-white { background-color: white !important; }
          .print\\:border-gray-300 { border-color: #d1d5db !important; }
        }
      `}</style>
    </div>
  );
};


// ── Metric Card sub-component ────────────────────────────
const MetricCard = ({ label, value, sub, subColor }) => (
  <div className="bg-gray-50 rounded-lg border p-4">
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    {sub && <div className={`text-xs mt-1 ${subColor || 'text-gray-500'}`}>{sub}</div>}
  </div>
);


export default SWEEReportViewer;
